import { post, read } from '@/utils/request';
import type { CurrentUserInfo } from '@/typings/user';
import type { UserAPI } from './interface';
import i18n from '@/i18n';
import type { CaptchaParams } from '@/hooks/useCaptcha';

const queryCurrent = () => {
  return read<CurrentUserInfo>(
    'user/user_info',
    undefined,
    i18n.t('failed_to_retrieve_user_information'),
  );
};

export type LoginInfo = {
  username: string;
  password: string;
  remember?: boolean;
  verify_code?: string;
};

const login = (params: LoginInfo) => {
  return post<CurrentUserInfo>('user/login', params, i18n.t('failed_to_login'));
};

// 同意用户协议
const userAgreement = () => {
  return read('user/user_agreement');
};

const logout = () => {
  return read<CurrentUserInfo>('user/logout', undefined, i18n.t('failed_to_logout'));
};

/** 注册 */
const register = (params: UserAPI.Request.RegisterParams) => {
  return post('user/register', params, i18n.t('failed_to_register'));
};

type IModifyPassword = {
  curPassword: string;
  password: string;
  rePassword: string;
};

const modifyPassword = (params: IModifyPassword) => {
  return post('user/modify_password', params, i18n.t('failed_to_modify_password'));
};

/** 忘记密码 */
const forgetPassword = (params: { email: string } & CaptchaParams) => {
  return post('user/forget_password', params, i18n.t('failed_to_operate'));
};

/** 获取短信验证码 */
const getCaptcha = (
  params: { phone: string; verify_type: UserAPI.Common.SmsCodeType } & CaptchaParams,
) => {
  return post('user/get_verify_code', params, i18n.t('failed_to_obtain_verification_code'));
};

/** 检查VL用户 邮箱激活状态 */
const getEmailStatus = (params: { email: string }) => {
  return post<UserAPI.Response.EmailStatus>('user/get_email_activate_status', params);
};

/** VL用户 重新发送激活邮件 */
const resendEmail = (params: { email: string } & CaptchaParams) => {
  return post('user/resend_email', params, i18n.t('failed_to_resend_email'));
};

/** 重置密码 */
const resetPassword = (params: UserAPI.Request.ResetPasswordParams) => {
  return post('user/reset_password', params, i18n.t('failed_to_reset_password'));
};

/** VL用户 激活 */
const activate = (params: { user_id: string; code: string }) => {
  return post<UserAPI.Response.ActivateStatus>('user/activate_vl_user', params);
};

/** VL用户信息修改 */
const modifyUserInfo = (params: UserAPI.Request.ModifyUserInfoParams) => {
  return post('user/modify_user_info', params, i18n.t('failed_to_modify_user_information'));
};

const sendVerifyCodeToEmail = (params: { email: string } & CaptchaParams) => {
  return post(
    'user/get_double_check_email_code',
    params,
    i18n.t('verify_email_send_failed'),
    undefined,
    {
      notice: false,
    },
  );
};

// 滑块验证接口
const verifyCaptcha = (params: { ticket: string; randstr: string }) => {
  return post('user/verify_captcha', params);
};

// 腾讯云密钥key
const getTencentCaptchaKey = () => {
  return read<UserAPI.Response.CaptchaKey>('user/get_encrypt_captcha_id');
};

export {
  register,
  login,
  userAgreement,
  logout,
  queryCurrent,
  modifyPassword,
  getCaptcha,
  forgetPassword,
  getEmailStatus,
  resendEmail,
  resetPassword,
  activate,
  modifyUserInfo,
  sendVerifyCodeToEmail,
  verifyCaptcha,
  getTencentCaptchaKey,
};
